import { useState } from "react";
import {
  VictoryChart,
  VictoryLine,
  VictoryAxis,
  VictoryTheme,
  VictoryBar,
  VictoryScatter,
  VictoryGroup,
  VictoryArea,
  VictoryVoronoiContainer,
  VictoryTooltip,
} from "victory";
import { FormControl, InputLabel, Select, MenuItem } from "@mui/material";

import { useVitalsData } from "../hooks/useVitalsData";
import { convertFromSnake } from "../utils";

const Vitals = ({ patient_id }) => {
  const {
    data: vitalsData,
    isLoading: isVitalsLoading,
    isError: isVitalsError,
    error: vitalsError,
  } = useVitalsData(patient_id);
  const [selectedField, setSelectedField] = useState(null);
  const [chartData, setChartData] = useState([]);

  // Handle the button click to show the selected field in a graph
  const handleFieldClick = (field) => {
    setSelectedField(field);

    // Prepare data for the Victory chart
    const chartData = vitalsData
      .filter((entry) => entry[field] !== 0 && typeof entry[field] === "number") // Filter out zero or non-numeric values
      .map((entry) => ({
        x: new Date(entry.measurement_time), // Use measurement_time for x-axis
        y: entry[field], // Use the selected field for y-axis
      }));

    setChartData(chartData);
  };

  const calculateMovingAverage = (data, window) => {
    if (!data || data.length < window) return [];

    return data
      .map((point, index) => {
        if (index < window - 1) return null;

        // Calculate average of last 'window' points
        let sum = 0;
        for (let i = 0; i < window; i++) {
          sum += data[index - i].y;
        }

        return {
          x: point.x,
          y: sum / window,
        };
      })
      .filter((point) => point !== null);
  };

  return (
    <div>
      {/* <h1>Vital Signs Dashboard</h1> */}
      {vitalsData.length > 0 && (
        <FormControl fullWidth variant="outlined" sx={{ minWidth: 200, mb: 2 }}>
          <InputLabel id="field-select-label">Select Field</InputLabel>
          <Select
            labelId="field-select-label"
            id="field-select"
            value={selectedField || ""}
            onChange={(e) => handleFieldClick(e.target.value)}
            label="Select Field"
          >
            {Object.keys(vitalsData[0])
              .filter((key) => key !== "measurement_time")
              .map((field) => (
                <MenuItem key={field} value={field}>
                  {convertFromSnake(field)}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
      )}

      {selectedField && (
        <div
          style={{
            width: "100%",
            maxWidth: 800,
            margin: "0px auto",
            // boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
            padding: "20px",
            borderRadius: "8px",
          }}
        >
          <h3
            style={{
              textAlign: "center",
              marginBottom: "20px",
              color: "#444",
            }}
          >
            {convertFromSnake(selectedField)} Measurements Over Time
          </h3>
          <VictoryChart
            theme={VictoryTheme.material}
            scale={{ x: "time" }}
            domainPadding={{ x: 20, y: 10 }}
            height={250} // Reduced height from default (usually 300)
            padding={{ top: 20, bottom: 50, left: 60, right: 30 }} // Adjusted padding
            containerComponent={
              <VictoryVoronoiContainer
                labels={({ datum }) =>
                  `${datum.y.toFixed(2)} on ${new Date(
                    datum.x
                  ).toLocaleDateString()}`
                }
                labelComponent={
                  <VictoryTooltip
                    cornerRadius={5}
                    flyoutStyle={{
                      fill: "white",
                      stroke: "#BFBFBF",
                    }}
                  />
                }
              />
            }
          >
            <VictoryAxis
              tickFormat={(t) => `${t.getDate()}/${t.getMonth() + 1}`}
              style={{
                axisLabel: { padding: 35 },
                tickLabels: { fontSize: 10, padding: 5 },
              }}
              label="Date"
            />
            <VictoryAxis
              dependentAxis
              label={convertFromSnake(selectedField)}
              style={{
                axisLabel: { padding: 40 },
                tickLabels: { fontSize: 10, padding: 5 },
              }}
              // Control the number of ticks to reduce y-axis height impression
              tickCount={4}
            />
            <VictoryGroup>
              <VictoryArea
                data={chartData}
                style={{
                  data: {
                    fill: "rgba(31, 210, 220, 0.2)",
                    stroke: "#1FD2DC",
                    strokeWidth: 2,
                  },
                }}
                interpolation="natural"
              />
              <VictoryScatter
                data={chartData}
                size={3} // Smaller points for the reduced height
                style={{
                  data: { fill: "#1FD2DC" },
                }}
              />
            </VictoryGroup>
            {/* Add moving average if there are enough data points */}
            {chartData.length > 3 && (
              <VictoryLine
                data={calculateMovingAverage(chartData, 3)}
                style={{
                  data: {
                    stroke: "#c43a31",
                    strokeWidth: 2,
                    strokeDasharray: "5,5",
                  },
                }}
                interpolation="natural"
              />
            )}
          </VictoryChart>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: "20px",
              marginTop: "10px",
            }}
          >
            <div style={{ display: "flex", alignItems: "center" }}>
              <div
                style={{
                  width: 12,
                  height: 12,
                  backgroundColor: "#1FD2DC",
                  marginRight: 5,
                }}
              ></div>
              <span>Actual values</span>
            </div>
            {chartData.length > 3 && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <div
                  style={{
                    width: 12,
                    height: 2,
                    backgroundColor: "#c43a31",
                    marginRight: 5,
                  }}
                ></div>
                <span>3-point moving average</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Show the chart when a field is selected */}
      {/* {selectedField && (
				<div style={{ width: "100%", margin: "50px auto" }}>
					<h3>Graph for {selectedField}</h3>

					<VictoryChart
						theme={VictoryTheme.material}
						scale={{ x: "time" }} // X-axis is time-based
					>
						<VictoryAxis
							tickFormat={(t) =>
								`${t.getDate()}/${t.getMonth() + 1}`
							} // Format ticks as day/month
						/>
						<VictoryAxis
							dependentAxis
							label={selectedField} // Y-axis label is the selected field
						/>

						<VictoryBar
							data={chartData}
							size={4}
							style={{
								data: { fill: "#1FD2DC" },
							}}
						/>
						<VictoryLine
							data={chartData} // Data for the line chart
							style={{
								data: { stroke: "#c43a31" },
								parent: { border: "1px solid #ccc" },
							}}
						/>
					</VictoryChart>
				</div>
			)} */}

      {/* Display buttons for each field in one row */}
    </div>
  );
};

export default Vitals;
